import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectAccountOnboarding,
  ConnectAccountManagement,
  ConnectComponentsProvider,
} from '@stripe/react-connect-js';
import { PAYMENTS_ACCOUNT_LABELS, paymentsAccountIsStale } from '@fsm/shared';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState } from './ui';

/**
 * Connecting this company's own card-payment account.
 *
 * The contractor's customers pay the contractor; Crewlark takes a fee off
 * the top and never holds the money. What that costs the contractor is one
 * button and Stripe's own form, which renders *inside this card* rather than
 * on a page they get bounced to.
 *
 * Nothing on this screen collects identity information. The bank account, the
 * EIN, the photo of somebody's licence — all of it is typed into Stripe's
 * component and goes straight to Stripe. This app stores an account id and
 * three booleans. That is deliberate: the moment a form here accepted a
 * passport scan, this project would own storing it safely and keeping the
 * verification rules current as laws change, forever.
 */

const TONE = {
  not_connected: 'slate',
  onboarding: 'amber',
  restricted: 'red',
  enabled: 'emerald',
} as const;

export function PaymentsCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: ['payments', 'account'],
    queryFn: () => api.paymentsAccount.get(),
  });

  /*
   * Failures are shown, not swallowed.
   *
   * These both talk to Stripe, which means they can fail for reasons nobody
   * here can guess — a key for the wrong account, a capability the platform
   * has not been granted, a country mismatch. Without somewhere to put the
   * message, pressing the button simply does nothing, which is the single
   * most useless thing an interface can do.
   */
  const [problem, setProblem] = useState<string | null>(null);
  const failed = (error: unknown) =>
    setProblem(error instanceof Error ? error.message : 'Something went wrong. Try again.');

  const create = useMutation({
    mutationFn: () => api.paymentsAccount.create(),
    onMutate: () => setProblem(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payments', 'account'] }),
    onError: failed,
  });

  const refresh = useMutation({
    mutationFn: () => api.paymentsAccount.refresh(),
    onMutate: () => setProblem(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payments', 'account'] }),
    onError: failed,
  });

  /*
   * Throw away a half-finished account and start again.
   *
   * Offered only while the account has never been able to charge, which is
   * exactly the state where starting over is harmless and sometimes the only
   * way forward — an account made against the wrong email, or onboarding
   * abandoned as the wrong legal entity. The server refuses it in any other
   * state regardless of what this screen shows.
   */
  const forget = useMutation({
    mutationFn: () => api.paymentsAccount.forget(),
    onMutate: () => setProblem(null),
    onSuccess: () => {
      setPolling(false);
      return qc.invalidateQueries({ queryKey: ['payments', 'account'] });
    },
    onError: failed,
  });

  const account = status.data;

  /*
   * Stripe's loader wants a function that fetches a fresh session secret, and
   * it calls that function again whenever a secret expires — so this must not
   * close over a cached value. Memoised on the account id alone: rebuilding
   * the connect instance remounts the component and loses the contractor's
   * half-filled form.
   */
  const fetchClientSecret = useCallback(async () => {
    const { clientSecret } = await api.paymentsAccount.session();
    return clientSecret;
  }, []);

  const connect = useMemo(() => {
    if (!account?.accountId || !account.publishableKey) return null;
    return loadConnectAndInitialize({
      publishableKey: account.publishableKey,
      fetchClientSecret,
      appearance: {
        /* Crewlark's green, so the form does not look bolted on. */
        variables: { colorPrimary: '#225d4d' },
      },
    });
  }, [account?.accountId, account?.publishableKey, fetchClientSecret]);

  /*
   * Stripe tells us an account is ready by webhook, which can be slow or can
   * be missed entirely. While somebody is sitting on this screen having just
   * finished the form, poll — but only in the state where the answer can
   * actually change, and stop as soon as it does.
   */
  const [polling, setPolling] = useState(false);
  useEffect(() => {
    if (!polling) return;
    if (account?.state === 'enabled') {
      setPolling(false);
      return;
    }
    const timer = setInterval(() => refresh.mutate(), 5000);
    /* Give up after a couple of minutes rather than polling forever. */
    const stop = setTimeout(() => setPolling(false), 120_000);
    return () => {
      clearInterval(timer);
      clearTimeout(stop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, account?.state]);

  if (status.isLoading) {
    return (
      <Card title="Card payments">
        <p className="px-4 py-5 text-[13px] text-ink-3">Checking…</p>
      </Card>
    );
  }

  if (account && !account.configured) {
    return (
      <Card title="Card payments">
        <EmptyState
          title="Card payments are not switched on for this server"
          description="No payment processor is configured yet. Until one is, invoices can still be sent and payments recorded by hand."
        />
      </Card>
    );
  }

  if (!account) {
    return (
      <Card title="Card payments">
        <EmptyState
          title="Could not load your payment setup"
          description="Try again in a moment."
        />
      </Card>
    );
  }

  const stale = paymentsAccountIsStale(account.syncedAt);

  return (
    <Card
      title="Card payments"
      action={
        <div className="flex items-center gap-2">
          <Badge tone={TONE[account.state]}>{PAYMENTS_ACCOUNT_LABELS[account.state]}</Badge>
          {account.accountId && canManage && (
            <Button size="sm" loading={refresh.isPending} onClick={() => refresh.mutate()}>
              Check again
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4 px-4 py-4">
        {problem && (
          <div className="rounded-[7px] bg-red-50 px-3 py-2.5 text-[13px] leading-relaxed text-red-700 ring-1 ring-inset ring-red-200">
            {problem}
          </div>
        )}

        {account.state === 'not_connected' && (
          <>
            <p className="text-[13px] leading-relaxed text-ink-2">
              Take card payments on invoices and at the door. Your customers pay you directly
              and the money goes to your own bank account — Crewlark never holds it.
            </p>
            <p className="text-[12.5px] leading-relaxed text-ink-3">
              Setting up takes a few minutes. You will need your business details and the bank
              account you want paying out to. Everything you enter goes straight to Stripe, our
              payment processor; Crewlark does not store any of it.
            </p>
            {canManage ? (
              <Button
                variant="primary"
                icon="card"
                loading={create.isPending}
                onClick={() => create.mutate()}
              >
                Set up payments
              </Button>
            ) : (
              <p className="text-[12.5px] text-ink-3">An admin can set this up.</p>
            )}
          </>
        )}

        {account.state === 'restricted' && (
          <p className="text-[13px] leading-relaxed text-ink-2">
            Card payments are paused on this account. Stripe needs something from you — the
            form below says what. Until it is sorted out, invoices can still be sent and
            payments recorded by hand.
          </p>
        )}

        {account.state === 'enabled' && !account.canPayout && (
          <p className="text-[13px] leading-relaxed text-ink-2">
            You can take payments. Payouts to your bank are not switched on yet, so the money
            is being held at Stripe until that is finished below.
          </p>
        )}

        {account.state === 'enabled' && account.canPayout && (
          <p className="text-[13px] leading-relaxed text-ink-2">
            You are set up to take card payments, and payouts are going to your bank account.
          </p>
        )}

        {/*
          Stripe's own component, rendered here. The onboarding form while they
          are still being verified; the management view afterwards, so a
          changed bank account or an expired document is something they fix
          themselves rather than ringing us about it.
        */}
        {connect && account.accountId && (
          <div className="rounded-[8px] border border-line p-3">
            <ConnectComponentsProvider connectInstance={connect}>
              {account.state === 'enabled' ? (
                <ConnectAccountManagement />
              ) : (
                <ConnectAccountOnboarding
                  onExit={() => {
                    /* They closed the form. Stripe may or may not have cleared
                       them; ask, and keep asking for a short while. */
                    refresh.mutate();
                    setPolling(true);
                  }}
                />
              )}
            </ConnectComponentsProvider>
          </div>
        )}

        {/*
          The way out of a stuck setup.

          Only while the account has never charged — once it has, the account
          may hold money and this is a conversation with Stripe rather than a
          button. Written plainly rather than hidden behind a gear icon,
          because the person who needs it is already frustrated.
        */}
        {account.state === 'onboarding' && account.accountId && canManage && (
          <div className="border-t border-line pt-3">
            <p className="text-[12.5px] leading-relaxed text-ink-3">
              Started this with the wrong details, or signed up as the wrong business?
              You can throw this away and begin again. Nothing has been charged.
            </p>
            <Button
              size="sm"
              icon="trash"
              className="mt-2"
              loading={forget.isPending}
              onClick={() => forget.mutate()}
            >
              Start over
            </Button>
          </div>
        )}

        {stale && account.accountId && (
          <p className="text-[12px] text-ink-4">
            Last checked with Stripe over a day ago. Press “Check again” if this looks wrong.
          </p>
        )}
      </div>
    </Card>
  );
}
